import React, { useState } from 'react';
import { Filter, Plus, Edit, Trash2, Eye, Settings, Target, Shield, AlertTriangle, Users, Building, DollarSign, Star, Check, X, ChevronRight, Upload } from 'lucide-react';

const Distribution = () => {
  const [activeTab, setActiveTab] = useState('filtering');
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [ruleType, setRuleType] = useState('filtering'); // 'filtering' ou 'exclusion'
  
  // État pour le formulaire de nouvelle règle
  const [newRule, setNewRule] = useState({
    name: '',
    type: 'inclusion',
    active: true,
    criteria: {
      bedrooms: { min: 1, max: 10 },
      minPrice: 10000,
      maxPrice: 100000,
      channels: [],
      amenities: [],
      minCommission: 5,
      destination: '',
      villaName: ''
    }
  });

  // Données des partenaires avec priorités
  const partners = [
    { id: 1, name: 'Le Collectionist', type: 'agency', priority: 9, channel: 'API', status: 'active', villas: 1247 },
    { id: 2, name: 'WIMCO', type: 'agency', priority: 8, channel: 'API', status: 'active', villas: 892 },
    { id: 3, name: 'Villa Novoapi', type: 'aggregator', priority: 6, channel: 'XML', status: 'active', villas: 543 },
    { id: 4, name: 'Luxury Retreats', type: 'agency', priority: 7, channel: 'API', status: 'active', villas: 678 },
    { id: 5, name: 'Propriétaires Directs', type: 'owner', priority: 10, channel: 'MANUAL', status: 'active', villas: 156 },
    { id: 6, name: 'Agences Locales', type: 'local_agency', priority: 7, channel: 'XML', status: 'active', villas: 234 }
  ];

  // Règles de filtrage actuelles
  const [filteringRules, setFilteringRules] = useState([
    {
      id: 1,
      name: 'Villas Premium Standard',
      type: 'inclusion',
      active: true,
      criteria: {
        bedrooms: { min: 1, max: 5 },
        minPrice: 15000,
        channels: ['WIMCO', 'Villa Novoapi', 'Luxuryretreat'],
        amenities: ['piscine'],
        minCommission: 10
      },
      stats: { matching: 2847, active: 2654 }
    }
  ]);

  // Règles d'exclusion
  const [exclusionRules, setExclusionRules] = useState([
    {
      id: 1,
      name: 'Villa NAO - Exclusion spécifique',
      type: 'villa_specific',
      active: true,
      criteria: { villaName: 'Villa NAO' },
      stats: { excluded: 1 }
    },
    {
      id: 2,
      name: 'Mykonos petites villas',
      type: 'location_criteria',
      active: true,
      criteria: { 
        destination: 'Mykonos', 
        bedrooms: { max: 2 }
      },
      stats: { excluded: 23 }
    }
  ]);

  // Statistiques de distribution
  const distributionStats = {
    totalVillas: 3750,
    eligibleVillas: 2847,
    activeVillas: 2654,
    excludedVillas: 193,
    duplicatesResolved: 456,
    averageCommission: 12.3
  };

  // Conflits de doublons résolus
  const duplicateResolutions = [
    {
      id: 1,
      villaName: 'Ocean Breeze Villa',
      location: 'Saint-Barthélemy',
      sources: [
        { partner: 'Le Collectionist', priority: 9, price: 15000, commission: 12 },
        { partner: 'Agence Locale St-Barth', priority: 7, price: 14500, commission: 10 },
        { partner: 'Propriétaire Direct', priority: 10, price: 13500, commission: 15 }
      ],
      selectedSource: 'Propriétaire Direct',
      reason: 'Priorité maximale (10) + Meilleur prix + Commission élevée'
    },
    {
      id: 2,
      villaName: 'Villa Sunset Paradise',
      location: 'Mykonos',
      sources: [
        { partner: 'WIMCO', priority: 8, price: 18000, commission: 11 },
        { partner: 'Villa Novoapi', priority: 6, price: 17500, commission: 8 }
      ],
      selectedSource: 'WIMCO',
      reason: 'Priorité supérieure (8 > 6) + Commission acceptable'
    }
  ];

  const getPriorityColor = (priority) => {
    if (priority >= 9) return 'text-green-600 bg-green-100';
    if (priority >= 7) return 'text-blue-600 bg-blue-100';
    if (priority >= 5) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getPriorityLabel = (priority) => {
    if (priority >= 9) return 'Excellent';
    if (priority >= 7) return 'Bon';
    if (priority >= 5) return 'Basique';
    return 'Faible';
  };

  // Fonctions pour gérer les règles
  const handleAddRule = (type = 'filtering') => {
    setRuleType(type);
    setNewRule({
      name: '',
      type: type === 'filtering' ? 'inclusion' : 'exclusion',
      active: true,
      criteria: {
        bedrooms: { min: 1, max: 10 },
        minPrice: 10000,
        maxPrice: 100000,
        channels: [],
        amenities: [],
        minCommission: 5,
        destination: '',
        villaName: ''
      }
    });
    setShowRuleModal(true);
  };

  const handleSaveRule = () => {
    const newId = Date.now(); // Simple ID generation
    const ruleToAdd = {
      ...newRule,
      id: newId,
      stats: {
        matching: Math.floor(Math.random() * 1000) + 100,
        active: Math.floor(Math.random() * 900) + 50,
        excluded: Math.floor(Math.random() * 50) + 1
      }
    };

    if (ruleType === 'filtering') {
      setFilteringRules([...filteringRules, ruleToAdd]);
    } else {
      setExclusionRules([...exclusionRules, ruleToAdd]);
    }

    setShowRuleModal(false);
    setNewRule({
      name: '',
      type: 'inclusion',
      active: true,
      criteria: {
        bedrooms: { min: 1, max: 10 },
        minPrice: 10000,
        maxPrice: 100000,
        channels: [],
        amenities: [],
        minCommission: 5,
        destination: '',
        villaName: ''
      }
    });
  };

  const handleDeleteRule = (id, type) => {
    if (type === 'filtering') {
      setFilteringRules(filteringRules.filter(rule => rule.id !== id));
    } else {
      setExclusionRules(exclusionRules.filter(rule => rule.id !== id));
    }
  };

  const handleRuleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setNewRule(prev => ({
        ...prev,
        criteria: {
          ...prev.criteria,
          [parent]: {
            ...prev.criteria[parent],
            [child]: value
          }
        }
      }));
    } else if (field === 'channels' || field === 'amenities') {
      setNewRule(prev => ({
        ...prev,
        criteria: {
          ...prev.criteria,
          [field]: Array.isArray(value) ? value : [value]
        }
      }));
    } else if (field.startsWith('criteria.')) {
      const criteriaField = field.replace('criteria.', '');
      setNewRule(prev => ({
        ...prev,
        criteria: {
          ...prev.criteria,
          [criteriaField]: value
        }
      }));
    } else {
      setNewRule(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const availableChannels = ['WIMCO', 'Villa Novoapi', 'Luxury Retreats', 'Le Collectionist', 'Agences Locales'];
  const availableAmenities = ['piscine', 'spa', 'gym', 'tennis', 'vue_mer', 'plage_privee', 'chef', 'majordome'];
  const availableDestinations = ['Saint-Barthélemy', 'Saint-Tropez', 'Mykonos', 'Ibiza', 'Cannes', 'Monaco', 'Courchevel'];

  return (
    <div className="distribution-management">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Distribution des Villas</h1>
            <p className="text-gray-600 mt-1">Gérez quelles villas apparaissent sur votre site public</p>
          </div>
          <div className="flex items-center space-x-3">
            <button className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              <Upload size={18} />
              <span>Synchroniser</span>
            </button>
            <button
              onClick={() => handleAddRule(activeTab === 'exclusions' ? 'exclusion' : 'filtering')}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <Plus size={18} />
              <span>Nouvelle Règle</span>
            </button>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Statistiques */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-6">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <Building className="text-gray-600" size={20} />
              <span className="text-xs text-gray-500">Total</span>
            </div>
            <div className="text-xl font-bold text-gray-800">{distributionStats.totalVillas.toLocaleString()}</div>
            <div className="text-sm text-gray-600">Villas Catalogue</div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <Check className="text-green-600" size={20} />
              <span className="text-xs text-green-600">+12.3%</span>
            </div>
            <div className="text-xl font-bold text-green-600">{distributionStats.eligibleVillas.toLocaleString()}</div>
            <div className="text-sm text-gray-600">Éligibles</div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <Eye className="text-blue-600" size={20} />
              <span className="text-xs text-blue-600">Live</span>
            </div>
            <div className="text-xl font-bold text-blue-600">{distributionStats.activeVillas.toLocaleString()}</div>
            <div className="text-sm text-gray-600">Affichées</div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <X className="text-red-600" size={20} />
              <span className="text-xs text-red-600">Filtrées</span>
            </div>
            <div className="text-xl font-bold text-red-600">{distributionStats.excludedVillas.toLocaleString()}</div>
            <div className="text-sm text-gray-600">Exclues</div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <Target className="text-purple-600" size={20} />
              <span className="text-xs text-purple-600">Auto</span>
            </div>
            <div className="text-xl font-bold text-purple-600">{distributionStats.duplicatesResolved.toLocaleString()}</div>
            <div className="text-sm text-gray-600">Doublons Résolus</div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="text-yellow-600" size={20} />
              <span className="text-xs text-yellow-600">Moy.</span>
            </div>
            <div className="text-xl font-bold text-yellow-600">{distributionStats.averageCommission}%</div>
            <div className="text-sm text-gray-600">Commission</div>
          </div>
        </div>

        {/* Onglets */}
        <div className="bg-white rounded-lg border border-gray-200 mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {[
                { id: 'filtering', label: 'Règles de Filtrage', icon: Filter },
                { id: 'exclusions', label: 'Exclusions', icon: Shield },
                { id: 'priorities', label: 'Priorités Partenaires', icon: Users },
                { id: 'duplicates', label: 'Gestion Doublons', icon: Target }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 py-4 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon size={18} />
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {/* Onglet Règles de Filtrage */}
            {activeTab === 'filtering' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-800">Règles d'Inclusion</h3>
                  <span className="text-sm text-gray-500">{filteringRules.length} règle(s) active(s)</span>
                </div>

                <div className="space-y-4">
                  {filteringRules.map((rule) => (
                    <div key={rule.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <div className={`w-3 h-3 rounded-full ${rule.active ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                          <h4 className="font-medium text-gray-800">{rule.name}</h4>
                          <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                            {rule.type === 'inclusion' ? 'Inclusion' : 'Exclusion'}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button className="text-gray-400 hover:text-blue-600">
                            <Edit size={16} />
                          </button>
                          <button 
                            onClick={() => handleDeleteRule(rule.id, 'filtering')}
                            className="text-gray-400 hover:text-red-600"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                        <div className="bg-gray-50 rounded p-3">
                          <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Chambres</div>
                          <div className="text-sm font-medium text-gray-800">
                            {rule.criteria.bedrooms?.min}-{rule.criteria.bedrooms?.max} chambres
                          </div>
                        </div>
                        <div className="bg-gray-50 rounded p-3">
                          <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Prix Minimum</div>
                          <div className="text-sm font-medium text-gray-800">
                            €{rule.criteria.minPrice?.toLocaleString()}/semaine
                          </div>
                        </div>
                        <div className="bg-gray-50 rounded p-3">
                          <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Commission Min</div>
                          <div className="text-sm font-medium text-gray-800">
                            {rule.criteria.minCommission}%+
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="text-sm text-gray-600">
                            <span className="font-medium text-green-600">{rule.stats.matching.toLocaleString()}</span> correspondances
                          </div>
                          <div className="text-sm text-gray-600">
                            <span className="font-medium text-blue-600">{rule.stats.active.toLocaleString()}</span> affichées
                          </div>
                        </div>
                        <div className="flex items-center space-x-2 text-xs text-gray-500">
                          {rule.criteria.channels?.map((channel, index) => (
                            <span key={index} className="px-2 py-1 bg-gray-100 rounded">
                              {channel}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Onglet Exclusions */}
            {activeTab === 'exclusions' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-800">Règles d'Exclusion</h3>
                  <span className="text-sm text-gray-500">{exclusionRules.length} règle(s) active(s)</span>
                </div>

                <div className="space-y-4">
                  {exclusionRules.map((rule) => (
                    <div key={rule.id} className="border border-red-200 rounded-lg p-4 bg-red-50">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <div className={`w-3 h-3 rounded-full ${rule.active ? 'bg-red-500' : 'bg-gray-300'}`}></div>
                          <h4 className="font-medium text-gray-800">{rule.name}</h4>
                          <span className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full">
                            {rule.type === 'villa_specific' ? 'Villa Spécifique' : 'Critère Localisation'}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button className="text-gray-400 hover:text-blue-600">
                            <Edit size={16} />
                          </button>
                          <button 
                            onClick={() => handleDeleteRule(rule.id, 'exclusion')}
                            className="text-gray-400 hover:text-red-600"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>

                      <div className="mb-3">
                        {rule.type === 'villa_specific' ? (
                          <div className="bg-white rounded p-3">
                            <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Villa Exclue</div>
                            <div className="text-sm font-medium text-gray-800">{rule.criteria.villaName}</div>
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="bg-white rounded p-3">
                              <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Destination</div>
                              <div className="text-sm font-medium text-gray-800">{rule.criteria.destination}</div>
                            </div>
                            <div className="bg-white rounded p-3">
                              <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Chambres Max</div>
                              <div className="text-sm font-medium text-gray-800">≤ {rule.criteria.bedrooms?.max} chambres</div>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-600">
                          <span className="font-medium text-red-600">{rule.stats.excluded}</span> villa(s) exclue(s)
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Onglet Priorités Partenaires */}
            {activeTab === 'priorities' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-800">Système de Priorités</h3>
                  <span className="text-sm text-gray-500">Priorité 10 = Maximum, 1 = Minimum</span>
                </div>

                <div className="space-y-4">
                  {partners.sort((a, b) => b.priority - a.priority).map((partner) => (
                    <div key={partner.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                            <Users className="text-blue-600" size={20} />
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-800">{partner.name}</h4>
                            <div className="flex items-center space-x-2 mt-1">
                              <span className="text-sm text-gray-500">{partner.type}</span>
                              <span className="text-gray-300">•</span>
                              <span className="text-sm text-gray-500">{partner.channel}</span>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center space-x-4">
                          <div className="text-right">
                            <div className="text-sm font-medium text-gray-800">
                              {partner.villas.toLocaleString()} villas
                            </div>
                            <div className="text-xs text-gray-500">
                              {partner.status === 'active' ? 'Actif' : 'Inactif'}
                            </div>
                          </div>

                          <div className="flex items-center space-x-2">
                            <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getPriorityColor(partner.priority)}`}>
                              Priorité {partner.priority}
                            </span>
                            <span className={`text-xs px-2 py-1 rounded-full ${getPriorityColor(partner.priority)}`}>
                              {getPriorityLabel(partner.priority)}
                            </span>
                          </div>

                          <button className="text-gray-400 hover:text-blue-600">
                            <Settings size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Onglet Gestion Doublons */}
            {activeTab === 'duplicates' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-800">Résolution Automatique des Doublons</h3>
                  <span className="text-sm text-gray-500">{duplicateResolutions.length} conflit(s) récent(s)</span>
                </div>

                <div className="space-y-6">
                  {duplicateResolutions.map((resolution) => (
                    <div key={resolution.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h4 className="font-medium text-gray-800">{resolution.villaName}</h4>
                          <p className="text-sm text-gray-500">{resolution.location}</p>
                        </div>
                        <span className="px-3 py-1 text-xs bg-green-100 text-green-800 rounded-full">Résolu</span>
                      </div>

                      <div className="grid grid-cols-1 gap-3 mb-4">
                        {resolution.sources.map((source, index) => (
                          <div 
                            key={index} 
                            className={`flex items-center justify-between p-3 rounded-lg ${
                              source.partner === resolution.selectedSource 
                                ? 'bg-green-50 border border-green-200' 
                                : 'bg-gray-50 border border-gray-200'
                            }`}
                          >
                            <div className="flex items-center space-x-3">
                              {source.partner === resolution.selectedSource && (
                                <Check className="text-green-600" size={16} />
                              )}
                              <div>
                                <div className="font-medium text-gray-800">{source.partner}</div>
                                <div className="text-xs text-gray-500">Priorité {source.priority}</div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="font-medium text-gray-800">€{source.price.toLocaleString()}</div>
                              <div className="text-xs text-gray-500">{source.commission}% commission</div>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <div className="flex items-start space-x-2">
                          <AlertTriangle className="text-blue-600 mt-0.5" size={16} />
                          <div>
                            <div className="text-sm font-medium text-blue-800">Décision Automatique</div>
                            <div className="text-sm text-blue-700">{resolution.reason}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal d'ajout de règle */}
      {showRuleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto m-4">
            {/* Header */}
            <div className={`${ruleType === 'filtering' ? 'bg-green-600' : 'bg-red-600'} text-white p-6 rounded-t-lg`}>
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold">
                    {ruleType === 'filtering' ? 'Nouvelle Règle de Filtrage' : 'Nouvelle Règle d\'Exclusion'}
                  </h2>
                  <p className="text-blue-100 mt-1">
                    {ruleType === 'filtering' 
                      ? 'Définissez les critères pour inclure les villas' 
                      : 'Définissez les critères pour exclure les villas'
                    }
                  </p>
                </div>
                <button
                  onClick={() => setShowRuleModal(false)}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); handleSaveRule(); }} className="p-6">
              {/* Section 1: Informations Générales */}
              <div className="mb-8">
                <div className="flex items-center mb-4">
                  <div className={`${ruleType === 'filtering' ? 'bg-green-100' : 'bg-red-100'} p-2 rounded-lg mr-3`}>
                    <Settings className={`${ruleType === 'filtering' ? 'text-green-600' : 'text-red-600'}`} size={20} />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800">Informations Générales</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Nom de la règle *</label>
                    <input
                      type="text"
                      required
                      value={newRule.name}
                      onChange={(e) => handleRuleInputChange('name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Ex: Villas Premium Méditerranée"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Type de règle</label>
                    <select
                      value={newRule.type}
                      onChange={(e) => handleRuleInputChange('type', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="inclusion">Inclusion (autoriser)</option>
                      <option value="exclusion">Exclusion (interdire)</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Statut</label>
                    <select
                      value={newRule.active}
                      onChange={(e) => handleRuleInputChange('active', e.target.value === 'true')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="true">Active</option>
                      <option value="false">Inactive</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Section 2: Critères de Villa */}
              <div className="mb-8">
                <div className="flex items-center mb-4">
                  <div className="bg-blue-100 p-2 rounded-lg mr-3">
                    <Building className="text-blue-600" size={20} />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800">Critères de Villa</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Chambres minimum</label>
                    <input
                      type="number"
                      min="1"
                      max="20"
                      value={newRule.criteria.bedrooms.min}
                      onChange={(e) => handleRuleInputChange('bedrooms.min', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Chambres maximum</label>
                    <input
                      type="number"
                      min="1"
                      max="20"
                      value={newRule.criteria.bedrooms.max}
                      onChange={(e) => handleRuleInputChange('bedrooms.max', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Destination</label>
                    <select
                      value={newRule.criteria.destination}
                      onChange={(e) => handleRuleInputChange('criteria.destination', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Toutes destinations</option>
                      {availableDestinations.map((dest) => (
                        <option key={dest} value={dest}>{dest}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Villa spécifique pour les exclusions */}
                {ruleType === 'exclusion' && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Villa spécifique (optionnel)</label>
                    <input
                      type="text"
                      value={newRule.criteria.villaName}
                      onChange={(e) => handleRuleInputChange('criteria.villaName', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Ex: Villa NAO"
                    />
                  </div>
                )}
              </div>

              {/* Section 3: Critères Financiers */}
              {ruleType === 'filtering' && (
                <div className="mb-8">
                  <div className="flex items-center mb-4">
                    <div className="bg-yellow-100 p-2 rounded-lg mr-3">
                      <DollarSign className="text-yellow-600" size={20} />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800">Critères Financiers</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Prix minimum (€/semaine)</label>
                      <input
                        type="number"
                        min="0"
                        step="1000"
                        value={newRule.criteria.minPrice}
                        onChange={(e) => handleRuleInputChange('criteria.minPrice', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Prix maximum (€/semaine)</label>
                      <input
                        type="number"
                        min="0"
                        step="1000"
                        value={newRule.criteria.maxPrice}
                        onChange={(e) => handleRuleInputChange('criteria.maxPrice', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Commission minimum (%)</label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.5"
                        value={newRule.criteria.minCommission}
                        onChange={(e) => handleRuleInputChange('criteria.minCommission', parseFloat(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Section 4: Canaux et Équipements */}
              {ruleType === 'filtering' && (
                <div className="mb-8">
                  <div className="flex items-center mb-4">
                    <div className="bg-purple-100 p-2 rounded-lg mr-3">
                      <Star className="text-purple-600" size={20} />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800">Canaux et Équipements</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Canaux autorisés</label>
                      <div className="space-y-2 max-h-32 overflow-y-auto border border-gray-200 rounded p-3">
                        {availableChannels.map((channel) => (
                          <label key={channel} className="flex items-center">
                            <input
                              type="checkbox"
                              checked={newRule.criteria.channels.includes(channel)}
                              onChange={(e) => {
                                const channels = e.target.checked 
                                  ? [...newRule.criteria.channels, channel]
                                  : newRule.criteria.channels.filter(c => c !== channel);
                                handleRuleInputChange('channels', channels);
                              }}
                              className="mr-2 rounded"
                            />
                            <span className="text-sm">{channel}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Équipements requis</label>
                      <div className="space-y-2 max-h-32 overflow-y-auto border border-gray-200 rounded p-3">
                        {availableAmenities.map((amenity) => (
                          <label key={amenity} className="flex items-center">
                            <input
                              type="checkbox"
                              checked={newRule.criteria.amenities.includes(amenity)}
                              onChange={(e) => {
                                const amenities = e.target.checked 
                                  ? [...newRule.criteria.amenities, amenity]
                                  : newRule.criteria.amenities.filter(a => a !== amenity);
                                handleRuleInputChange('amenities', amenities);
                              }}
                              className="mr-2 rounded"
                            />
                            <span className="text-sm capitalize">{amenity.replace('_', ' ')}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowRuleModal(false)}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className={`px-6 py-2 ${ruleType === 'filtering' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'} text-white rounded-lg transition-colors font-medium`}
                >
                  Créer la Règle
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Distribution;